// react
import { useEffect, useMemo, useState } from "react";

// styled components
import { ThemeProvider } from "styled-components";
import { StyledApp } from "./StyledApp";

// components
import ModelViewer from "components/ModelViewer/ModelViewer";
import Tutorial from "components/Tutorial/Tutorial";
import ForVRmaker from "components/ForVRmaker/ForVRmaker";
import SideBarNav from "components/SideBarNav/SideBarNav";
import ModalPopup from "components/ModalPopup/ModalPopup";
import FailPage from "components/FailPage/FailPage";
import ArStatusPopup from "components/ArStatusPopup/ArStatusPopup";
import CookieBanner from "components/CookieBanner/CookieBanner";

// constants
import THEME from "constants/theme";

// hooks
import useModal from "hooks/useModal";

// config
import { modalConfig } from "config/modal";

// api
import { getProductInfo } from "api/product/product.api";

// react query
import { useQuery } from "react-query";

// plugin
import Cookies from "js-cookie";
import * as dayjs from "dayjs";

// i18n
import { useTranslation } from "react-i18next";

// constants
import SERVER from "constants/server";
import ThreeJs from "../../components/ThreeJs/ThreeJs";

// utils
import { isIOSDevice } from "utils/device";
import { createPageViewTracker, getObjectId, getObjectType } from "utils/analytics";
import { getVisitId } from "utils/visitor";
import { getBackgroundSourceModel, getModelBackground, toCssBackground } from "utils/background";

// GA page_view：一次頁面載入只送一次。正常在 getProductInfo 有結果時送（帶 object_type），
// API 遲遲沒結果時，於 window load 後短暫等待再送只帶 object_id 的版本。時機與頻寬取捨見 utils/analytics。
const gaPageView = createPageViewTracker({
  measurementId: SERVER.GA_MEASUREMENT_ID,
  objectId: getObjectId(window.location.search),
  visitId: getVisitId(),
});
gaPageView.sendFallbackAfterLoad();

const App = () => {
  const { t, i18n } = useTranslation();
  const { modalSetting, setModalSetting } = useModal();

  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  const uid = decodeURIComponent(urlParams.get("uid"));
  Cookies.set("uid", uid, { domain: SERVER.GET_DOMAIN, sameSite: "none", secure: true });
  const {
    data: resProductInfo,
    isLoading,
    error,
  } = useQuery("getProductInfo", getProductInfo(uid));

  const [productInfo, setProductInfo] = useState([]);
  const [loadingStatus, setloadingStatus] = useState(true);
  const [err, setErr] = useState(false);

  const [modelViewrIndex, setIndex] = useState(0);
  const [isAlphaBg, setAlphanBg] = useState(false);
  const [isCtrl, setCtrl] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [wiggle, setWiggle] = useState(true);

  const [pan, setPan] = useState(true);
  const [rotationPerSecond, setRotationPerSecond] = useState("11.25deg");

  const [isGlobalSwitch, setIsGlobalSwitch] = useState(false);
  const [showBar, setShowBar] = useState(false);

  // 為了vr-maker 客製化
  const [forVRmaker, setForVRmaker] = useState(false);
  const [hiddenModel, setHiddenModel] = useState(false);
  const [hiddenAnimation, setHiddenAnimation] = useState(false);

  const [currentModelFileType, setCurrentModelFileType] = useState(null);
  const [currentModel, setCurrentModel] = useState(null);

  // default parameters
  useEffect(() => {
    if (urlParams.get("lang")) {
      i18n.changeLanguage(urlParams.get("lang"));
    } else if (Cookies.get("lang")) {
      i18n.changeLanguage(Cookies.get("lang"));
    }

    if (urlParams.get("modelIndex")) {
      setIndex(parseFloat(urlParams.get("modelIndex")));
    }

    if (urlParams.get("disableBackground") === "true") {
      setAlphanBg(true);
      setCtrl(true);
    } else if (urlParams.get("disableBackground")) {
      setCtrl(true);
    }

    if (urlParams.get("autoRotate")) {
      setAutoRotate(urlParams.get("autoRotate") === "true");
    }

    if (urlParams.get("wiggle")) {
      setWiggle(urlParams.get("wiggle") === "true");
    }

    if (urlParams.get("pan")) {
      setPan(urlParams.get("pan") === "true");
    }

    if (urlParams.get("rotationPerSecond")) {
      setRotationPerSecond(urlParams.get("rotationPerSecond"));
    }

    // 為了vr-maker 客製化
    if (urlParams.get("onlyForVRmaker")) {
      setForVRmaker(urlParams.get("onlyForVRmaker"));
      setShowBar(false);
      setHiddenAnimation(true);
    }
  }, []);

  // getProductInfo 有結果（成功或失敗）就送 GA page_view
  useEffect(() => {
    if (isLoading) return;
    gaPageView.send(getObjectType(resProductInfo));
  }, [isLoading, resProductInfo]);

  // 判斷是否正常連線，再判斷是否有這筆資料
  useEffect(() => {
    let netststus = navigator.onLine;
    if (netststus) {
      if (!resProductInfo?.result && resProductInfo?.status === "failed") {
        // QRCode失效(找不到該頁面)
        setErr(true);
        return;
      }
      if (resProductInfo) {
        setIsGlobalSwitch(resProductInfo.onlineProduct.onlineProductEditors[0].globalSwitch);
        let sortAry = resProductInfo.onlineProduct.onlineProductEditors[0].onlineProductModels.sort(
          (a, b) => {
            return dayjs(b.lastUpdated).unix() - dayjs(a.lastUpdated).unix();
          },
        );

        for (let i = 0; i < sortAry.length; i++) {
          if (sortAry[i].mainObject === true) {
            let mainTemp = sortAry[i];
            sortAry[i] = sortAry[0];
            sortAry[0] = mainTemp;
          }
        }
        setProductInfo(sortAry);
      }
    } else {
      // 網路連線有問題，再試一次
      setModalSetting({
        ...modalSetting,
        show: true,
        title: "",
        type: "type15",
        coverSetting: true,
        handleOtherAction: true,
        otherBtnText: t("ErrResponse.PleaseTryAgain"),
        handleConfirm: () => {
          window.location.reload();
        },
      });
    }
  }, [resProductInfo]);

  const [isAr, setIsAr] = useState(false);
  const [noUsdz, setNoUsdz] = useState(false);
  const [arStatus, setArStatus] = useState("");

  useEffect(() => {
    setCurrentModel(productInfo[modelViewrIndex]);
    const fileType = productInfo[modelViewrIndex]?.lastUploadFileType;
    if (fileType) {
      setCurrentModelFileType(fileType);
    }
    // 判斷是否開啟AR投放開關，有則顯示AR按鈕 (viewModeSwitch:null/true/false)
    setIsAr(productInfo[modelViewrIndex]?.viewModeSwitch !== false);
    setNoUsdz(productInfo[modelViewrIndex]?.usdzName === "");

    // 判斷是否使用AR投放，有則開始偵測裝置
    if (urlParams.get("openAr")) {
      setArStatus("偵測裝置中");

      if (productInfo.length > 0) {
        // 偵測作業系統（含桌面模式 iPad）
        let isIOS = isIOSDevice();
        // 偵測瀏覽器
        let isChrome = navigator.userAgent.indexOf("CriOS") > -1;
        let isSafari = navigator.userAgent.indexOf("Safari") > -1;
        if (isChrome && isSafari) isSafari = false;

        const getMovelViewer = document.querySelector("model-viewer");

        const detectAndActivateAR = () => {
          // 以 model-viewer 的 canActivateAR 能力偵測決定能否投放。
          // 採 feature detection，平板即使切換「桌面版網站」模式也能正確判斷。
          if (!getMovelViewer?.canActivateAR) {
            // AR投放無法在目前的裝置使用（電腦版裝置）
            setArStatus("無法使用");
            return;
          }
          // 判斷使用者是否開啟AR投放開關
          if (productInfo[modelViewrIndex]?.viewModeSwitch === false) {
            // 未開啟AR投放開關
            setArStatus("無法使用");
            return;
          }
          // 判斷使用者是否使用IOS作業系統
          if (isIOS) {
            // 判斷物件是否缺少USDZ檔案
            if (productInfo[modelViewrIndex]?.usdzName === "") {
              // 缺少USDZ檔案，則無法開啟AR投放
              setArStatus("無法使用");
            } else if (isSafari) {
              // 判斷使用者是否使用Safari瀏覽器
              setArStatus("偵測裝置成功");
              getMovelViewer.activateAR();
            } else {
              // 使用非Safari瀏覽器，則無法開啟AR投放
              setArStatus("未使用Safari瀏覽器");
            }
          } else {
            // 其餘系統不需判斷檔案，直接開啟AR投放
            setArStatus("偵測裝置成功");
            getMovelViewer.activateAR();
          }
        };

        if (!getMovelViewer) {
          // 無 model-viewer 元素（例如 Gaussian Splat 模型），無法投放
          setArStatus("無法使用");
        } else if (getMovelViewer.loaded) {
          // 模型已載入，直接偵測
          detectAndActivateAR();
        } else {
          // 等模型載入後再偵測，canActivateAR 此時才準確
          getMovelViewer.addEventListener("load", detectAndActivateAR);
        }
      }
    }
  }, [productInfo, modelViewrIndex]);

  const isGSModel = useMemo(() => currentModelFileType?.includes("splat"), [currentModelFileType]);

  // 目前這個模型的背景（每個模型各自設定；切換 modelIndex 時跟著換）。
  // ScanKit 物件例外：背景在 ScanKit 編輯頁設在照片那個模型上，物件附帶的 3D 版本（splat）也沿用同一個
  const background = useMemo(
    () => getModelBackground(getBackgroundSourceModel(productInfo, currentModel)),
    [productInfo, currentModel],
  );

  return (
    <ThemeProvider theme={THEME}>
      <StyledApp isAlphaBg={isAlphaBg} forVRmaker={forVRmaker} $background={toCssBackground(background)}>
        {arStatus && <ArStatusPopup type={arStatus} modelIndex={modelViewrIndex} />}
        {err ? (
          <FailPage />
        ) : isGSModel ? (
          <ThreeJs
            setloadingStatus={setloadingStatus}
            currentModel={currentModel}
            // disableBackground 時維持改版前的白底（點雲原本就不支援透明，這次不改變這個行為）
            background={isAlphaBg ? { type: "default" } : background}
          />
        ) : (
          <ModelViewer
            modelList={productInfo}
            modelIndex={modelViewrIndex}
            setloadingStatus={setloadingStatus}
            loadingStatus={loadingStatus}
            autoRotate={autoRotate}
            wiggle={wiggle}
            pan={pan}
            rotationPerSecond={rotationPerSecond}
            showBar={showBar}
            setShowBar={setShowBar}
            hiddenModel={hiddenModel}
            hiddenAnimation={hiddenAnimation}
            background={background}
          />
        )}
        {!isGSModel && !isCtrl && isGlobalSwitch && (
          <SideBarNav
            modelList={productInfo}
            modelIndex={modelViewrIndex}
            setIndex={setIndex}
            loadingStatus={loadingStatus}
            setloadingStatus={setloadingStatus}
            showBar={showBar}
            setShowBar={setShowBar}
          />
        )}
        {!isCtrl && (
          <Tutorial
            isAr={isAr}
            modelIndex={modelViewrIndex}
            loadingStatus={loadingStatus}
            productInfo={productInfo}
            noUsdz={noUsdz}
            isGSModel={isGSModel}
          />
        )}
        {forVRmaker && (
          <ForVRmaker
            modelIndex={modelViewrIndex}
            setArStatus={setArStatus}
            noUsdz={noUsdz}
            setHiddenModel={setHiddenModel}
          />
        )}
        {modalSetting.show && (
          <ModalPopup
            modalConfig={modalConfig[modalSetting.type]}
            setModalSetting={setModalSetting}
            modalSetting={modalSetting}
          />
        )}
        <CookieBanner />
      </StyledApp>
    </ThemeProvider>
  );
};

export default App;
